import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserSummaryPanel = ({ 
  participants, 
  billItems, 
  currentUserId = '1',
  onProceedToPayment,
  position = 'floating' // 'floating' | 'sidebar'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const calculateUserTotal = (userId) => {
    let subtotal = 0;
    
    billItems?.forEach(item => {
      const userClaim = item?.claims?.find(claim => claim?.userId === userId);
      if (userClaim) {
        subtotal += item?.price * userClaim?.quantity;
      }
    });

    // IVA ya incluido en los precios del ticket
    const tax = 0;
    const total = subtotal;

    return { subtotal, tax, total };
  };

  const getUserClaimedItems = (userId) => {
    const claimedItems = [];
    
    billItems?.forEach(item => {
      const userClaim = item?.claims?.find(claim => claim?.userId === userId);
      if (userClaim) {
        claimedItems?.push({
          ...item,
          claimedQuantity: userClaim?.quantity,
          itemTotal: item?.price * userClaim?.quantity
        });
      }
    });

    return claimedItems;
  };

  const getTotalUnassignedItems = () => {
    return billItems?.reduce((total, item) => {
      const claimedQuantity = item?.claims?.reduce((sum, claim) => sum + claim?.quantity, 0) || 0;
      return total + (item?.quantity - claimedQuantity);
    }, 0);
  };

  const getCompletionPercentage = () => {
    const totalItems = billItems?.reduce((sum, item) => sum + item?.quantity, 0);
    const claimedItems = billItems?.reduce((sum, item) => {
      const claimed = item?.claims?.reduce((claimSum, claim) => claimSum + claim?.quantity, 0) || 0;
      return sum + claimed;
    }, 0);
    
    return totalItems > 0 ? Math.round((claimedItems / totalItems) * 100) : 0;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(price);
  };

  const currentUser = participants?.find(p => p?.id === currentUserId);
  const userTotal = calculateUserTotal(currentUserId);
  const userItems = getUserClaimedItems(currentUserId);
  const unassignedItems = getTotalUnassignedItems();
  const completionPercentage = getCompletionPercentage();

  if (position === 'floating') {
    return (
      <>
        {/* Floating Summary Button */}
        <div className="fixed bottom-6 right-6 z-40">
          <Button
            variant="default"
            size="lg"
            onClick={() => setIsExpanded(!isExpanded)}
            className="rounded-full shadow-elevation-3 hover:shadow-elevation-3 transition-all duration-300"
            iconName="Receipt"
            iconSize={20}
          >
            <span className="hidden sm:inline ml-2">
              Mi Total: {formatPrice(userTotal?.total)}
            </span>
            <span className="sm:hidden ml-2">
              {formatPrice(userTotal?.total)}
            </span>
          </Button>
        </div>
        {/* Expanded Summary Panel */}
        {isExpanded && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/20 z-30"
              onClick={() => setIsExpanded(false)}
            />
            
            {/* Summary Panel */}
            <div className="fixed bottom-24 right-6 z-40 bg-surface border border-border rounded-lg shadow-elevation-3 p-4 w-80 max-w-[calc(100vw-3rem)] max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Mi Resumen</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(false)}
                  className="h-8 w-8"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>

              {/* User Info */}
              <div className="flex items-center space-x-3 mb-4 p-3 bg-muted/30 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                  {currentUser?.name?.charAt(0) || 'T'}
                </div>
                <div>
                  <div className="font-medium text-foreground">Tú</div>
                  <div className="text-sm text-text-secondary">
                    {userItems?.length} artículo{userItems?.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Items List */}
              {userItems?.length > 0 ? (
                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-medium text-foreground">Mis Artículos:</h4>
                  {userItems?.map((item) => (
                    <div key={item?.id} className="flex items-center justify-between text-sm">
                      <div className="flex-1">
                        <span className="text-foreground">{item?.name}</span>
                        <span className="text-text-secondary ml-2">×{(() => {
                          if (item?.shared && Array.isArray(item?.sharedWith) && item.sharedWith.length > 0) {
                            const perShare = item.quantity / item.sharedWith.length;
                            const shares = Math.round((item.claimedQuantity / perShare) * 100) / 100;
                            const display = Math.abs(shares - Math.round(shares)) < 0.01 ? Math.round(shares) : shares;
                            return `${display} / ${item.sharedWith.length}`;
                          }
                          return item?.claimedQuantity;
                        })()}</span>
                      </div>
                      <span className="font-medium text-foreground">
                        {formatPrice(item?.itemTotal)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-text-secondary">
                  <Icon name="ShoppingCart" size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No has seleccionado ningún artículo</p>
                </div>
              )}

              {/* Total Breakdown */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Subtotal:</span>
                  <span className="text-foreground">{formatPrice(userTotal?.subtotal)}</span>
                </div>
                {/* IVA ya incluido en precios del ticket */}
                {userTotal?.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">IVA (21%):</span>
                    <span className="text-foreground">{formatPrice(userTotal?.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-foreground border-t border-border pt-2">
                  <span>Total:</span>
                  <span>{formatPrice(userTotal?.total)}</span>
                </div>
              </div>

              {/* Progress Info */}
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-text-secondary">Progreso de la mesa:</span>
                  <span className="text-sm font-medium text-foreground">{completionPercentage}%</span>
                </div>
                <div className="w-full bg-border rounded-full h-2 mb-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                {unassignedItems > 0 && (
                  <p className="text-xs text-text-secondary">
                    {unassignedItems} artículo{unassignedItems !== 1 ? 's' : ''} sin asignar
                  </p>
                )}
              </div>

              {/* Action Button */}
              <Button
                variant="default"
                fullWidth
                onClick={onProceedToPayment}
                disabled={userItems?.length === 0}
                iconName="CreditCard"
                iconPosition="left"
                iconSize={16}
                className="mt-4"
              >
                Proceder al Pago
              </Button>
            </div>
          </>
        )}
      </>
    );
  }

  // Sidebar version
  return (
    <div className="w-80 bg-surface border-l border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Resumen Personal
        </h2>
        
        {/* User Info */}
        <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
            {currentUser?.name?.charAt(0) || 'U'}
          </div>
          <div>
            <div className="font-medium text-foreground">{currentUser?.name || 'Usuario'}</div>
            <div className="text-sm text-text-secondary">
              {userItems?.length} artículo{userItems?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Items List */}
        {userItems?.length > 0 ? (
          <div className="space-y-3 mb-6">
            <h3 className="font-medium text-foreground">Mis Artículos:</h3>
                  {userItems?.map((item) => (
              <div key={item?.id} className="p-3 bg-card border border-border rounded-lg">
                <div className="flex items-start justify-between mb-1">
                  <span className="font-medium text-foreground">{item?.name}</span>
                  <span className="font-semibold text-foreground">
                    {formatPrice(item?.itemTotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-text-secondary">
                  <span>{formatPrice(item?.price)} × {(() => {
                    if (item?.shared && Array.isArray(item?.sharedWith) && item.sharedWith.length > 0) {
                      const perShare = item.quantity / item.sharedWith.length;
                      const shares = Math.round((item.claimedQuantity / perShare) * 100) / 100;
                      const display = Math.abs(shares - Math.round(shares)) < 0.01 ? Math.round(shares) : shares;
                      return `${display} / ${item.sharedWith.length}`;
                    }
                    return item?.claimedQuantity;
                  })()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="ShoppingCart" size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No has seleccionado ningún artículo</p>
            <p className="text-xs mt-1">Selecciona artículos de la lista para ver tu resumen</p>
          </div>
        )}

        {/* Progress Section */}
        <div className="mb-6">
          <h3 className="font-medium text-foreground mb-3">Progreso de la Mesa</h3>
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Completado:</span>
              <span className="text-sm font-medium text-foreground">{completionPercentage}%</span>
            </div>
            <div className="w-full bg-border rounded-full h-2 mb-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            {unassignedItems > 0 && (
              <p className="text-xs text-text-secondary">
                {unassignedItems} artículo{unassignedItems !== 1 ? 's' : ''} sin asignar
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        {/* Total Breakdown */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Subtotal:</span>
            <span className="text-foreground">{formatPrice(userTotal?.subtotal)}</span>
          </div>
          {/* IVA ya incluido en precios del ticket */}
          {userTotal?.tax > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">IVA (21%):</span>
              <span className="text-foreground">{formatPrice(userTotal?.tax)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-foreground border-t border-border pt-2">
            <span>Total:</span>
            <span>{formatPrice(userTotal?.total)}</span>
          </div>
        </div>

        {/* Action Button */}
        <Button
          variant="default"
          fullWidth
          onClick={onProceedToPayment}
          disabled={userItems?.length === 0}
          iconName="CreditCard"
          iconPosition="left"
          iconSize={16}
        >
          Proceder al Pago
        </Button>
      </div>
    </div>
  );
};

export default UserSummaryPanel;